/**
 * Properly formats a number to a fixed number of decimal places
 * by truncating instead of rounding to avoid floating-point precision issues
 *
 * @param value - The number to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string with exact decimal places
 */
export function formatNumberFixed(
  value: number | string,
  decimals: number = 2
): string {
  const num = typeof value === "string" ? parseFloat(value) : value;

  if (isNaN(num)) {
    return "0.00";
  }

  // Multiply by 10^decimals, truncate, then divide back
  const multiplier = Math.pow(10, decimals);
  const truncated = Math.floor(num * multiplier) / multiplier;

  return truncated.toFixed(decimals);
}

/**
 * Alternative method using string manipulation for even more precision
 *
 * @param value - The number to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string with exact decimal places
 */
export function formatNumberTruncate(
  value: number | string,
  decimals: number = 2
): string {
  const str = typeof value === "number" ? value.toString() : value;
  const num = parseFloat(str);

  if (isNaN(num)) {
    return "0.00";
  }

  // Convert to string and find decimal point
  const numStr = num.toString();
  const decimalIndex = numStr.indexOf(".");

  if (decimalIndex === -1) {
    // No decimal point, add zeros
    return numStr + "." + "0".repeat(decimals);
  }

  // Truncate at the desired decimal places
  const truncated = numStr.substring(0, decimalIndex + decimals + 1);
  const result = parseFloat(truncated);

  return result.toFixed(decimals);
}
